<script lang="ts">
  import { onMount } from 'svelte';
  import { Mic, ChevronRight } from 'lucide-svelte';
  import Button from './ui/button.svelte';
  import Card from './ui/card.svelte';
  import { supabase } from '../lib/supabase';
  import { selectedTargetLanguage, flashcardActions } from '../stores/flashcards';
  import { _ } from 'svelte-i18n';
  import TargetLanguageDialog from './dialogs/TargetLanguageDialog.svelte';
  import ConversationDialog from './dialogs/ConversationDialog.svelte';
  import { getFlagUrl } from '../lib/languageFlags';
  import type { Database } from '../integrations/supabase/types';

  type Category = Database['public']['Tables']['flashcard_categories']['Row'];
  type Level = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';

  // DialogueSession type (from migration, not yet in generated types)
  type DialogueSession = {
    id: string;
    user_id: string;
    status: string;
    topic_id: string;
    level: string;
    language_id: string;
    metadata: any;
    started_at: string;
    ended_at: string | null;
    duration_seconds: number | null;
    created_at: string;
    updated_at: string;
  };

  // State
  let showLanguageDialog = $state(false);
  let showConversationDialog = $state(false);
  let categories = $state<Category[]>([]);
  let recentConversations = $state<DialogueSession[]>([]);
  let selectedCategory = $state<Category | null>(null);
  let userLevel = $state<Level>('B2');
  let isLoadingCategories = $state(false);

  onMount(async () => {
    flashcardActions.loadData();
    await loadUserLevel();
    await loadCategories();
    await loadRecentConversations();
  });

  async function loadUserLevel() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_settings')
        .select('proficiency_level')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Failed to load user level:', error);
        return;
      }

      if (data?.proficiency_level) {
        userLevel = data.proficiency_level as Level;
      }
    } catch (err) {
      console.error('Error loading user level:', err);
    }
  }

  async function loadCategories() {
    if (!$selectedTargetLanguage) return;

    isLoadingCategories = true;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('flashcard_categories')
        .select('*')
        .eq('target_language_id', $selectedTargetLanguage.id)
        .or(`user_id.eq.${user.id},user_id.is.null`)
        .order('name');

      if (error) throw error;

      // Remove duplicates by id and name
      const uniqueCategories = new Map<string, Category>();
      (data || []).forEach(cat => {
        const existing = uniqueCategories.get(cat.id);
        if (!existing) {
          uniqueCategories.set(cat.id, cat);
        }
      });

      categories = Array.from(uniqueCategories.values());
    } catch (err) {
      console.error('Failed to load categories:', err);
    } finally {
      isLoadingCategories = false;
    }
  }

  async function loadRecentConversations() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Use direct query since dialogue_sessions might not be in generated types yet
      const { data, error } = await supabase
        .from('dialogue_sessions' as any)
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'ended')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        console.error('Error loading conversations:', error);
        return;
      }

      recentConversations = data || [];
    } catch (err) {
      console.error('Failed to load conversations:', err);
    }
  }

  async function startConversation(category: Category) {
    selectedCategory = category;
    showConversationDialog = true;
  }

  function handleConversationEnd() {
    showConversationDialog = false;
    selectedCategory = null;
    loadRecentConversations();
  }

  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  function formatTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleTimeString('cs-CZ', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function formatDuration(seconds: number | null): string {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    return `(${mins}m)`;
  }

  function getCategoryName(topicId: string): string {
    const category = categories.find(c => c.id === topicId);
    return category?.name || topicId;
  }

  // Reload categories when language changes
  $effect(() => {
    if ($selectedTargetLanguage) {
      loadCategories();
    }
  });
</script>

<div class="min-h-screen pb-24">
  <div class="mx-auto max-w-md px-4 py-8 space-y-8">
    <!-- Language Selection, Category and Start Button - Single Card -->
    {#if $selectedTargetLanguage}
      <Card class="widget-card tone-header p-6">
        <div class="space-y-3">
          <!-- Language Selection Header -->
          <div class="flex items-start justify-between gap-4">
            <div class="flex-1">
              <p class="section-label">{$_('speak.selected_language')}</p>
              <button
                type="button"
                class="flex mt-2 items-center gap-3 font-semibold leading-tight text-foreground transition hover:text-foreground/90 focus:outline-none"
                onclick={() => (showLanguageDialog = true)}
              >
                <div class="h-12 w-16 rounded-2xl bg-foreground/10 shadow-soft-sm overflow-hidden flex items-center justify-center">
                  {#if $selectedTargetLanguage}
                    <img
                      src={getFlagUrl($selectedTargetLanguage.code, 'w80')}
                      alt={$selectedTargetLanguage?.name || ''}
                      class="h-full w-full object-cover"
                      loading="lazy"
                      onerror={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    />
                  {/if}
                </div>
                <p class="text-xl font-semibold text-foreground">
                  {$selectedTargetLanguage?.name || $_('speak.select_language')}
                </p>
              </button>
            </div>
            <div class="rounded-2xl widget-muted px-4 py-3 text-right">
              <p class="section-label text-[0.68rem] tracking-[0.24em]">{$_('speak.level')}</p>
              <p class="text-2xl font-semibold text-foreground">
                {userLevel}
              </p>
            </div>
          </div>

          <!-- Category Select -->
          <div class="space-y-2">
            <label for="category-select" class="section-label">
              {$_('speak.select_category')}
            </label>
            {#if isLoadingCategories}
              <div class="w-full rounded-2xl border-2 border-foreground/20 bg-foreground/5 px-4 py-3 text-muted-foreground">
                {$_('speak.loading_categories')}
              </div>
            {:else if categories.length === 0}
              <div class="w-full rounded-2xl border-2 border-foreground/20 bg-foreground/5 px-4 py-3 text-muted-foreground">
                {$_('speak.no_categories')}
              </div>
            {:else}
              <select
                id="category-select"
                bind:value={selectedCategory}
                class="w-full appearance-none rounded-2xl border-2 border-foreground/20 bg-white px-4 py-3 text-base font-semibold transition-all cursor-pointer focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                style="color: #1e293b;"
              >
                <option value={null} disabled selected style="color: #94a3b8;">
                  {$_('speak.choose_category')}
                </option>
                {#each categories as category}
                  <option value={category} style="color: #1e293b;">
                    {category.name}
                  </option>
                {/each}
              </select>
            {/if}
          </div>

          <!-- Start Conversation Button -->
          <Button
            size="lg"
            disabled={!selectedCategory || isLoadingCategories}
            onclick={() => selectedCategory && startConversation(selectedCategory)}
            class="w-full"
          >
            <div class="flex items-center gap-2">
              <Mic class="h-5 w-5" />
              <span>{$_('speak.start_conversation')}</span>
            </div>
          </Button>
        </div>
      </Card>
    {:else}
      <Card class="widget-card tone-list p-8 text-center">
        <p class="text-muted-foreground mb-4">{$_('speak.please_select_language')}</p>
        <Button onclick={() => (showLanguageDialog = true)}>
          {$_('speak.select_language')}
        </Button>
      </Card>
    {/if}

    <!-- Recent Conversations -->
    {#if recentConversations.length > 0}
      <div class="space-y-4">
        <h2 class="section-title">{$_('speak.last_conversations')}</h2>
        <div class="space-y-3">
          {#each recentConversations as conversation}
            <Card class="widget-card tone-list p-5">
              <div class="flex items-center justify-between gap-4">
                <div class="flex-1">
                  <div class="flex items-center gap-3 mb-1">
                    <div class="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span class="text-sm font-semibold text-primary">
                        {conversation.topic_id.substring(0, 2).toUpperCase()}
                      </span>
                    </div>
                    <div class="flex-1 min-w-0">
                      <h3 class="font-semibold text-foreground truncate">
                        {getCategoryName(conversation.topic_id)}
                      </h3>
                      <p class="text-xs text-muted-foreground">
                        {formatDate(conversation.created_at)} {formatTime(conversation.created_at)} {formatDuration(conversation.duration_seconds)}
                      </p>
                    </div>
                  </div>
                </div>
                <ChevronRight class="h-5 w-5 text-muted-foreground flex-shrink-0" />
              </div>
            </Card>
          {/each}
        </div>
      </div>
    {/if}
  </div>
</div>

<!-- Language Selection Dialog -->
<TargetLanguageDialog
  open={showLanguageDialog}
  onClose={() => (showLanguageDialog = false)}
/>

<!-- Conversation Dialog -->
{#if showConversationDialog && selectedCategory}
  <ConversationDialog
    open={showConversationDialog}
    category={selectedCategory}
    level={userLevel}
    onClose={handleConversationEnd}
  />
{/if}
